import { Boxes, Rocket, Bot, BarChart3 } from "lucide-react";

const STEPS = [
  {
    icon: Boxes,
    title: "Browse or generate",
    body: "Pick a scenario from the role-filtered catalog, or describe one and let AI generate a fresh box for you.",
  },
  {
    icon: Rocket,
    title: "Spin up your range",
    body: "One click provisions an isolated attack box on cloud infrastructure — open it in the browser or connect over VPN.",
  },
  {
    icon: Bot,
    title: "Train with AI copilots",
    body: "Work the challenge with on-page and in-box assistants, and design networks by conversation on the canvas.",
  },
  {
    icon: BarChart3,
    title: "Track & score",
    body: "Answers map to TKSA coverage and MITRE ATT&CK, then roll up to the dashboard built for your role.",
  },
];

const RING_RADII = [120, 200, 280, 360];
const BLIPS = [
  { top: "34%", left: "62%", delay: "0.4s" },
  { top: "60%", left: "38%", delay: "3.2s" },
  { top: "46%", left: "30%", delay: "5s" },
  { top: "66%", left: "63%", delay: "2s" },
];

function RadarBackground() {
  const discMask = "radial-gradient(closest-side, #000 62%, transparent 100%)";
  const pingBorder = {
    border:
      "1px solid color-mix(in srgb, var(--color-primary) 45%, transparent)",
    transform: "translate(-50%, -50%)",
  };
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-[58%] z-0 h-130 w-130 -translate-x-1/2 -translate-y-1/2 opacity-50 sm:h-170 sm:w-170"
      style={{ maskImage: discMask, WebkitMaskImage: discMask }}
    >
      {/* range rings + crosshairs */}
      <svg viewBox="0 0 760 760" className="absolute inset-0 h-full w-full">
        {RING_RADII.map((r) => (
          <circle
            key={r}
            cx="380"
            cy="380"
            r={r}
            fill="none"
            stroke="var(--color-primary)"
            strokeOpacity="0.16"
            strokeWidth="1"
          />
        ))}
        <line
          x1="380"
          y1="20"
          x2="380"
          y2="740"
          stroke="var(--color-primary)"
          strokeOpacity="0.09"
        />
        <line
          x1="20"
          y1="380"
          x2="740"
          y2="380"
          stroke="var(--color-primary)"
          strokeOpacity="0.09"
        />
        <line
          x1="90"
          y1="90"
          x2="670"
          y2="670"
          stroke="var(--color-primary)"
          strokeOpacity="0.06"
        />
        <line
          x1="670"
          y1="90"
          x2="90"
          y2="670"
          stroke="var(--color-primary)"
          strokeOpacity="0.06"
        />
      </svg>

      {/* rotating sweep beam */}
      <div
        className="lp-radar-spin absolute inset-0 rounded-full"
        style={{
          background:
            "conic-gradient(from 0deg, color-mix(in srgb, var(--color-primary) 32%, transparent) 0deg, color-mix(in srgb, var(--color-primary) 5%, transparent) 26deg, transparent 60deg, transparent 360deg)",
          maskImage: discMask,
          WebkitMaskImage: discMask,
        }}
      />

      {/* sonar pings */}
      <span
        className="lp-radar-ping absolute left-1/2 top-1/2 h-28 w-28 rounded-full"
        style={pingBorder}
      />
      <span
        className="lp-radar-ping absolute left-1/2 top-1/2 h-28 w-28 rounded-full"
        style={{ ...pingBorder, animationDelay: "2.5s" }}
      />

      {/* blips */}
      {BLIPS.map((b, i) => (
        <span
          key={i}
          className="lp-radar-blip absolute h-1.5 w-1.5 rounded-full"
          style={{
            top: b.top,
            left: b.left,
            background: "var(--color-primary)",
            boxShadow: "0 0 10px var(--color-primary)",
            animationDelay: b.delay,
          }}
        />
      ))}
    </div>
  );
}

export default function Workflow() {
  return (
    <section className="relative isolate overflow-hidden py-16 lg:py-22">
      <RadarBackground />

      <div className="relative z-10 mx-auto max-w-350 px-5 md:px-10">
        <div className="lp-reveal lp-down mx-auto max-w-2xl text-center">
          <div className="mb-4 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-(--color-primary)">
            <span className="h-px w-6 bg-(--color-primary)" />
            How it works
            <span className="h-px w-6 bg-(--color-primary)" />
          </div>
          <h1
            className="lp-rise qv-glitch mt-6 text-[32px] sm:text-[40px] leading-tight font-normal tracking-tight text-(--color-text-primary)"
            style={{ "--lp-delay": "90ms" }}
          >
            {" "}
            From zero to hacking in{" "}
            <span className="lp-highlight">four steps</span>
          </h1>
        </div>

        <div className="relative mt-14">
          {/* connecting line behind the badges (lg only) */}
          <div className="pointer-events-none absolute left-0 right-0 top-6 hidden h-px bg-[linear-gradient(90deg,transparent,var(--color-border-solid)_15%,var(--color-border-solid)_85%,transparent)] lg:block" />

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  style={{ transitionDelay: `${i * 120}ms` }}
                  className="lp-reveal lp-down relative text-center"
                >
                  <div className="relative z-10 mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-(--color-border-solid) bg-(--color-bg) text-(--color-primary) shadow-[0_0_18px_var(--color-shadow-primary)]">
                    <Icon className="h-5 w-5" />
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-(--color-primary) text-[10px] font-bold text-(--color-text-on-primary)">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="mt-5 text-[15px] font-semibold text-(--color-text-primary)">
                    {step.title}
                  </h3>
                  <p className="mx-auto mt-2 max-w-60 text-[13px] leading-relaxed text-(--color-text-secondary)">
                    {step.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
